import {
  Avatar,
  DropdownButtonContext,
  DropdownSelect,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ReadSearchApiArg, SearchUserEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { getStatus, PresenceStatus } from 'components/presence/utils';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import { ConfirmTransferModal } from './components/confirmTransferModal/confirmTransferModal';
import { RequestTransferAssistButton } from './components/requestTransferAssistButton/requestTransferAssistButton';
import { TransferToAgentSelectItem } from './components/transferToAgentSelectItem/transferToAgentSelectItem';
import styles from './transferToAgentDropdown.module.scss';
import { comparePresenceStatus } from './utils/comparePresenceStatus';
import { isAgentAvailableForCall } from './utils/isAgentAvailableForCall';
import { sortTransferToAgentItems } from './utils/sortTransferToAgentItems';

export type TransferToAgentItem = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  status: PresenceStatus;
  member: SearchUserEnriched;
  actions: React.ReactNode;
};

export const TransferToAgentDropdown = (): React.ReactElement => {
  const [transferToUser, setTransferToUser] = useState<TransferToAgentItem>();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    assignedAgent,
    metaConversation,
    transferToAgent,
    conversationAsyncStatus,
  } = useAppSelector(
    (state) => ({
      assignedAgent:
        state.conversation.metaConversation.conversation.assignment?.agent,
      metaConversation: state.conversation.metaConversation,
      conversationAsyncStatus: state.conversation.asyncStatus.getConversation,
      transferToAgent: state.transferToAgent,
    }),
    shallowEqual,
  );

  const inProgress = useMemo((): boolean => {
    return !!transferToAgent.transferRequests.find(
      (request) =>
        request?.metaConversation?.conversation?.id ===
        metaConversation.conversation.id,
    );
  }, [transferToAgent, metaConversation]);

  const [readSearchAgents, { results: agents, isFetching, updateResults }] =
    useLazyReadSearchInfiniteQuery<'users', true>();

  const buildSearchArg = useCallback(
    (query?: string): ReadSearchApiArg<'users', true> => ({
      scope: 'users',
      query: query || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ value: 'true' }],
          },
        ],
      },
      sortBy: { field: 'display_name', order: 'asc' },
      pageSize: 100,
      enrichmentSpec: ['workload_config'],
    }),
    [],
  );

  // Re-run the search as the agent types. The infinite-query hook strips
  // `sortBy` for non-empty queries so search relevance takes precedence.
  useUpdateEffect(() => {
    readSearchAgents(buildSearchArg(searchQuery || undefined), {
      debounceTime: 300,
    });
  }, [readSearchAgents, buildSearchArg, searchQuery]);

  const items: readonly TransferToAgentItem[] = useMemo(() => {
    const channel = metaConversation.conversation.initial_channel;

    return agents
      .map((member): TransferToAgentItem => {
        const canBeCalled = isAgentAvailableForCall(member);

        const showCallButton =
          channel === 'pstnphone' &&
          canBeCalled &&
          member.id !== assignedAgent?.id;

        return {
          id: member.id,
          name: getUserDisplayName(member),
          status: getStatus(member),
          avatarUrl: member.avatar_url,
          member,
          actions: showCallButton && (
            <RequestTransferAssistButton user={member} />
          ),
        };
      })
      .sort(comparePresenceStatus);
  }, [agents, metaConversation, assignedAgent]);

  const disabledItemsIds: TransferToAgentItem['id'][] = useMemo(() => {
    const channel = metaConversation.conversation.initial_channel;
    let disabledPresences: string[] = [];

    if (channel === 'pstnphone' || channel === 'callback') {
      const disabledPresencesForPhone = items
        .filter((item) => !isAgentAvailableForCall(item.member))
        .map((item) => item.id);

      disabledPresences = [...disabledPresences, ...disabledPresencesForPhone];
    }

    return assignedAgent
      ? [assignedAgent.id, ...disabledPresences]
      : disabledPresences;
  }, [items, assignedAgent, metaConversation]);

  const sortedItems = useMemo(
    () => sortTransferToAgentItems(items, disabledItemsIds),
    [items, disabledItemsIds],
  );

  const handleOnToggle = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('');
      // Clear results so a reopen shows the loading state and fetches fresh,
      // rather than briefly showing the previous session's (possibly stale)
      // presence data.
      updateResults((draft) => {
        draft.length = 0;
      });
      return;
    }

    ampli.conversationHeaderTransferToAgentMenuOpened();
    readSearchAgents(buildSearchArg(searchQuery || undefined), {
      debounceTime: 0,
    });
  };

  const handleOnAgentSelect = (selectItem: TransferToAgentItem) => {
    ampli.conversationHeaderTransferToAgentItemSelected();
    setTransferToUser(selectItem);
    setSearchQuery('');
  };

  const emptyMessage =
    isFetching && !searchQuery
      ? 'Loading agents...'
      : searchQuery
        ? 'No search results...'
        : 'No agents available...';

  const dropdownButtonClassName = classNames(
    {
      [styles.dropdownButtonHasAvatar]: !inProgress,
    },
    styles.dropdownButton,
  );

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:transferToAgent">
      Transfer to agent
    </CommandKeybindingsTooltipContent>
  );

  return (
    <>
      <Tooltip
        content={tooltipContent}
        disabled={inProgress}
        wrap="div"
        wrapProps={{
          className: styles.tooltip,
        }}
      >
        <DropdownSelect
          items={sortedItems}
          itemComponent={TransferToAgentSelectItem}
          selectedItems={assignedAgent?.id ? [assignedAgent?.id] : undefined}
          disabledItems={disabledItemsIds}
          searchPlaceholder="Search for online agents..."
          onSelect={handleOnAgentSelect}
          onToggle={handleOnToggle}
          onSearch={(value) => setSearchQuery(value !== undefined ? value : '')}
          hasSearch={true}
          noArrow={inProgress ? true : false}
          emptyMessage={emptyMessage}
          fixed={true}
          className={styles.dropdownSelect}
          dropdownProps={{
            className: styles.dropdown,
          }}
          buttonProps={{
            variant: 'tertiary',
            disabled: inProgress,
            isLoading: conversationAsyncStatus === 'loading',
            className: dropdownButtonClassName,
            slotStart: inProgress ? undefined : (
              <Avatar user={assignedAgent} size="24" />
            ),
          }}
        >
          {!inProgress && (
            <DropdownButtonContext.Consumer>
              {({ dropdownOpen, openDropdown, closeDropdown }) => (
                <TransferToAgentCommandHandler
                  actionFn={() => {
                    if (!dropdownOpen) {
                      openDropdown();
                    } else {
                      closeDropdown();
                    }
                    handleOnToggle(!dropdownOpen);
                  }}
                />
              )}
            </DropdownButtonContext.Consumer>
          )}
          {inProgress && 'Transfer in progress...'}
          {!inProgress && assignedAgent && `${assignedAgent.name}`}
          {!inProgress && !assignedAgent && 'Unassigned'}
        </DropdownSelect>
      </Tooltip>

      {assignedAgent && transferToUser && metaConversation && (
        <ConfirmTransferModal
          assignedAgent={assignedAgent}
          transferToUser={transferToUser}
          onClose={() => setTransferToUser(undefined)}
          isOpen={!!transferToUser}
          metaConversation={metaConversation}
        />
      )}
    </>
  );
};

const TransferToAgentCommandHandler = ({
  actionFn,
}: {
  actionFn: () => void;
}): null => {
  useCommandHandler({
    name: 'conversation:transferToAgent',
    actionFn: () => {
      actionFn();
    },
  });

  return null;
};
